import { isSafari } from '@/utils/browser-kit';
import { extractBaseTextInfoFromTextLayer, getListMarkerChars, TextMeasurer } from '@/utils/text-kit';
import {
    TextWritingType,
    type BaseTextInfo,
    type ILayerProps,
    type ITextContent,
    type ITextLayerProps,
    type ITextMark,
} from '@/version-types';

type TextNode = { node: Node; textContent?: ITextContent; listOrder?: number };

type BaseFragment = {
    text: string;

    width: number;
    height: number;
    top: number;
    bottom: number;
    left: number;
};

export type TextFragment = BaseFragment & {
    line: number;

    fontSize: number;

    listOrder?: number;

    children?: BaseFragment[];

    style?: {
        fontSize: number;
        fontFamily: string;
        color: string;
        bold?: boolean;
        italic?: boolean;
        underline?: boolean;
        lineThrough?: boolean;
        rotate?: number;
        textMark?: ITextMark;
    };
};

export class TextFragmentCreator {
    private constructor() {
        throw new Error('TextFragmentCreator is a static class');
    }

    /**
     * 获取列表项文本片段
     * @param listOrder - 列表项序号
     * @param baseTextInfo - 基础文本信息
     * @param line - 行号
     * @returns 列表项文本片段
     */
    private static getListMarkerFragment(
        listOrder: number,
        baseTextInfo: BaseTextInfo,
        line: number,
        options = { offset: 0 },
    ) {
        const { fontSize, listStyle } = baseTextInfo;

        const layout = TextMeasurer.measureListMarkerLayout(listOrder, baseTextInfo, options);

        const fragment: TextFragment = {
            text:
                listStyle !== 'custom'
                    ? getListMarkerChars(listStyle!, listOrder, {
                          followDot:
                              listStyle === 'decimal' || listStyle === 'lower-latin' || listStyle === 'upper-roman',
                      })
                    : '',
            line,
            fontSize,
            listOrder,
            ...layout,
        };

        return fragment;
    }

    /**
     * 获取模拟布局容器内所有的有效文本节点（列表文本场景下）
     * @param container - 模拟布局容器
     * @param layer - 图层数据
     * @returns
     */
    private static getTextNodesWithListText(container: HTMLDivElement, layer: ILayerProps) {
        const $ul = container.querySelector('ul');
        if (!$ul) return [] as TextNode[];

        const layerData = layer.data as ITextLayerProps;
        const { textContents = [] } = layerData;

        const children = $ul.children; // li collection
        if (children.length === 0) return [] as TextNode[];

        const textNodes: TextNode[] = [];
        for (let i = 0; i < children.length; i++) {
            const $li = children[i] as HTMLLIElement;
            const order = i + 1;

            const walker = document.createTreeWalker($li, NodeFilter.SHOW_TEXT, {
                acceptNode(node) {
                    const parentNode = node.parentNode;
                    if ((parentNode as HTMLElement).hasAttribute('data-mock-marker')) {
                        return NodeFilter.FILTER_SKIP;
                    }
                    return NodeFilter.FILTER_ACCEPT;
                },
            });

            let idx = 0;

            while (walker.nextNode()) {
                const node = walker.currentNode;
                const parentNode = node.parentNode;

                if (parentNode && parentNode.nodeName === 'SPAN') {
                    let textContent: ITextContent | undefined = undefined;

                    const span = parentNode as HTMLSpanElement;
                    const dataset = span.getAttribute('data-origin-idx');
                    const textContentIdx = typeof dataset === 'undefined' ? -1 : Number(dataset);
                    if (textContents.length > 0 && textContentIdx >= 0 && textContentIdx < textContents.length) {
                        textContent = textContents[textContentIdx];
                    }

                    textNodes.push({ node, textContent, listOrder: idx === 0 ? order : undefined });
                } else {
                    textNodes.push({ node, listOrder: idx === 0 ? order : undefined });
                }

                idx++;
            }
        }

        return textNodes;
    }

    /**
     * 获取模拟布局容器内所有的有效文本节点（非列表文本场景下）
     * @param container - 模拟布局容器
     * @param layer - 图层数据
     * @returns
     */
    private static getTextNodesWithoutListText(container: HTMLDivElement, layer: ILayerProps) {
        const childNodes = container.childNodes;
        if (childNodes.length === 0) return [] as TextNode[];

        const layerData = layer.data as ITextLayerProps;
        const { textContents = [] } = layerData;

        const textNodes: TextNode[] = [];

        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
            const node = walker.currentNode;
            const parentNode = node.parentNode;

            let textContent: ITextContent | undefined = undefined;

            if (parentNode && parentNode.nodeName === 'SPAN') {
                const span = parentNode as HTMLSpanElement;
                const dataset = span.getAttribute('data-origin-idx');
                const textContentIdx = typeof dataset === 'undefined' ? -1 : Number(dataset);
                if (textContents.length > 0 && textContentIdx >= 0 && textContentIdx < textContents.length) {
                    textContent = textContents[textContentIdx];
                }
            }

            textNodes.push({ node: walker.currentNode, textContent });
        }

        return textNodes;
    }

    /**
     * 根据 textNode 的内容，获取富文本片段的样式
     * @param textNode - 文本节点信息
     * @param layer - 图层数据
     * @returns
     */
    private static getFragmentScopedStyle(textNode: TextNode, layer: ILayerProps): TextFragment['style'] {
        const { textContent } = textNode;
        if (!textContent) return undefined;

        const fontFamilyMap = layer.__runtime?.fontFamilyMap || {};
        const layerData = layer.data as ITextLayerProps;

        const primaryFontFamily = layerData.fontFamily;
        const fallbackFontFamily = fontFamilyMap[primaryFontFamily] || primaryFontFamily;
        const scopedFontFamily = textContent.fontFamily;
        const finalFontFamily = fontFamilyMap[scopedFontFamily] || scopedFontFamily || fallbackFontFamily;

        const style: TextFragment['style'] = {
            fontSize: textContent.fontSize,
            fontFamily: finalFontFamily,
            color: textContent.color,
            bold: textContent.bold,
            italic: textContent.italic,
            underline: textContent.underline,
            lineThrough: textContent.lineThrough,
        };

        if (textContent.rotate) style.rotate = textContent.rotate;
        if (textContent.textMark) style.textMark = textContent.textMark;

        return style;
    }

    /**
     * 获取默认的文本片段
     * @param line - 行号
     * @param fontSize - 字体大小
     * @returns
     */
    private static getDefaultFragment(line: number, fontSize: number): TextFragment {
        const fragment: TextFragment = {
            text: '',
            width: 0,
            height: 0,
            bottom: 0, // HACK: safari bug，需要借助 bottom 辅助判断是否折行，
            top: 0,
            left: 0,
            line,
            fontSize,
        };
        return fragment;
    }

    private static createFragments(container: HTMLDivElement, layer: ILayerProps) {
        const baseTextInfo = extractBaseTextInfoFromTextLayer(layer);
        const layerData = layer.data as ITextLayerProps;
        const { listStyle, writingMode, fontSize, lineSpacing } = layerData;
        const isVertical = writingMode !== TextWritingType.HORIZONTAL_TB;

        const textNodes = listStyle
            ? this.getTextNodesWithListText(container, layer)
            : this.getTextNodesWithoutListText(container, layer);

        if (textNodes.length === 0) return [] as TextFragment[];

        const fragments: TextFragment[] = [];

        const range = document.createRange();
        const containerRect = container.getBoundingClientRect();

        let line = 1;

        for (const textNode of textNodes) {
            const { node, listOrder } = textNode;

            // 过滤掉无效的文本节点
            if (node.textContent === '') continue;

            // --- 1. 设置列表项标记 ---
            // 若存在列表项标记，需要优先将列表项作为一个 fragment 添加到 fragments 中
            let listMarkerFragment: TextFragment | null = null;
            if (listOrder) {
                if (listOrder !== 1) line++;

                // 列表项偏移（场景：矢量图层文字内容可能会存在内边距，需要将列表项的偏移量进行调整）
                const offset = (() => {
                    const padding = layer.__padding;
                    if (!padding) return undefined;
                    return isVertical ? padding.top : padding.left;
                })();
                listMarkerFragment = this.getListMarkerFragment(
                    listOrder,
                    baseTextInfo,
                    line,
                    typeof offset !== 'undefined' ? { offset } : undefined,
                );
                if (listMarkerFragment) fragments.push(listMarkerFragment);
            }

            // --- 2. 初始化文本片段样式 ---
            const fragmentStyle = this.getFragmentScopedStyle(textNode, layer);

            // --- 3. 开始处理文本片段 ---
            let fragment = this.getDefaultFragment(line, fragmentStyle ? fragmentStyle.fontSize : fontSize);
            let children: BaseFragment[] = [];

            const text = node.textContent || ' ';
            const segments = new Intl.Segmenter().segment(text);

            for (const segData of segments) {
                const char = segData.segment;
                const startIdx = segData.index;
                const endIdx = startIdx + segData.segment.length;
                range.setStart(node, startIdx);
                range.setEnd(node, endIdx);

                let rect = range.getBoundingClientRect();

                // HACK: 修复 Safari 下通过 range.getBoundingClientRect 获取 rect 不符合预期的问题
                // 理论上 rects 的长度只可能为 1
                // 但是在 safari 中，会把折行处也算作一个 rect
                // 比如：“双击编辑文字”，在“辑”处自动折行，那么获取“文”的 rects 时，长度就会为 2
                if (isSafari) {
                    const rects = range.getClientRects();
                    const len = rects.length;

                    if (len > 1) rect = rects[len - 1];
                }

                const width = rect.width;
                const height = rect.height;
                const bottom = rect.bottom;
                const left = rect.left - containerRect.left;
                const top = rect.top - containerRect.top;

                const charFragment: BaseFragment = {
                    text: char,
                    width,
                    height,
                    top,
                    left,
                    bottom,
                };

                if (fragment.text.length === 0) {
                    if (fragments.length > 0) {
                        const prev = fragments[fragments.length - 1];
                        const isNewLine = top - prev.top + 1 >= prev.fontSize * lineSpacing;
                        if (isNewLine) line++;
                    }

                    fragment = { ...fragment, ...charFragment, line };
                    children.push(charFragment);
                } else {
                    const isNewLine = isVertical
                        ? Math.abs(left - fragment.left) > 0
                        : top !== fragment.top || bottom !== fragment.bottom;

                    if (isNewLine) {
                        fragment.children = children;
                        fragment.style = fragmentStyle;
                        fragments.push(fragment);

                        // HACK: fixed safari text rect bug
                        if (isSafari && top === fragment.top) charFragment.top = top + (bottom - fragment.bottom);

                        line++;

                        children = [charFragment];
                        fragment = {
                            ...charFragment,
                            line,
                            fontSize: fragmentStyle ? fragmentStyle.fontSize : fontSize,
                        };
                    } else {
                        fragment.text += char;

                        // HACK: 以下代码对 top 和 left 重新取值为了处理 RTL 文本计算反向的问题比如阿拉伯文和希伯来文
                        // JIRA: https://jira.meitu.com/browse/MTXXPRO-58370

                        if (isVertical) {
                            fragment.height += height;
                            fragment.top = Math.min(fragment.top, charFragment.top);
                        } else {
                            fragment.width += width;
                            fragment.left = Math.min(fragment.left, charFragment.left);
                        }

                        children.push(charFragment);
                    }
                }
            }

            if (fragment.text) {
                fragment.style = fragmentStyle;
                fragment.children = children;
                fragment.line = line;
                fragments.push(fragment);
            }
        }

        return fragments;
    }

    /**
     * 创建文本片段
     * @param container - 文本容器
     * @param layer - 图层数据
     * @returns
     */
    static create(layer: ILayerProps) {
        const baseTextInfo = extractBaseTextInfoFromTextLayer(layer);
        const padding = layer.__padding || { top: 0, right: 0, bottom: 0, left: 0 };
        const { writingMode } = baseTextInfo;
        const isVertical = writingMode !== TextWritingType.HORIZONTAL_TB;

        const layout = TextMeasurer.createVirtualLayoutInfo(layer, {
            keepConstantWidth: true,
            keepConstantHeight: isVertical,
            preservePadding: padding,
        });

        if (!layout || !layout.$box) return [];

        const container = layout.$box;
        const fragments = this.createFragments(container, layer);

        console.log('TextFragmentCreator: ', fragments);

        return fragments;
    }
}
